import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useBlocker } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"
import { FiDownload, FiMoreVertical, FiRotateCcw, FiSave } from "react-icons/fi"
import { ClientDataExportApi } from "../../api/clientDataExport"
import {
  type CompanyProfileDTO,
  type CompanyProfileLookupFromGUSResponse,
  CompanyProfileService,
} from "../../api/companyProfile"
import { downloadBlob } from "../../utils/downloadBlob"
import ConfirmDialog from "../Common/ConfirmDialog"
import { WmsPageHeader } from "../WMS/shared/WmsPageHeader"
import CompanyProfileForm from "./CompanyProfileForm"
import CompanyProfileGUSDialog from "./CompanyProfileGUSDialog"
import CompanyProfileHistory from "./CompanyProfileHistory"
import {
  type CompanyProfileDraft,
  type CompanyProfileErrors,
  type CompanyProfileField,
  draftToUpdateRequest,
  isCompanyProfileDirty,
  isValidPolishNip,
  profileToDraft,
  validateCompanyProfileDraft,
} from "./companyProfileForm"

const sectionLinks = [
  ["company-identity", "Tożsamość firmy"],
  ["company-contact", "Kontakt i adres"],
  ["company-regional", "Ustawienia regionalne"],
  ["company-branding", "Branding"],
] as const

type CompanyProfileSaveAttempt = {
  draft: CompanyProfileDraft
  expectedRevisionNumber: number
  labelLogoFile: File | null
}

export default function CompanySettingsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const gusDialog = useDisclosure()
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const stickyBg = useColorModeValue("whiteAlpha.950", "gray.900")
  const [baseline, setBaseline] = useState<CompanyProfileDTO | null>(null)
  const [draft, setDraft] = useState<CompanyProfileDraft | null>(null)
  const [errors, setErrors] = useState<CompanyProfileErrors>({})
  const [gusResult, setGUSResult] =
    useState<CompanyProfileLookupFromGUSResponse | null>(null)
  const [revisionConflict, setRevisionConflict] = useState<number | null>(null)
  const [serverHasNewerRevision, setServerHasNewerRevision] = useState(false)
  const [saveOutcomeUnknown, setSaveOutcomeUnknown] = useState(false)
  const [labelLogoFile, setLabelLogoFile] = useState<File | null>(null)
  const [labelLogoFileReady, setLabelLogoFileReady] = useState(false)

  const profileQuery = useQuery({
    queryKey: ["companyProfile", "authenticated"],
    queryFn: () => CompanyProfileService.getProfile(),
    retry: 1,
  })

  const isDirty = Boolean(
    labelLogoFile ||
      (baseline &&
        draft &&
        isCompanyProfileDirty(profileToDraft(baseline), draft)),
  )
  const requiresNewLabelLogoFile = Boolean(
    draft?.labelLogoSource === "upload" &&
      baseline?.labelLogoSource !== "upload" &&
      !labelLogoFile,
  )
  const labelLogoUploadInvalid =
    requiresNewLabelLogoFile || Boolean(labelLogoFile && !labelLogoFileReady)

  useEffect(() => {
    if (!profileQuery.data) return
    if (!baseline || !draft) {
      setBaseline(profileQuery.data)
      setDraft(profileToDraft(profileQuery.data))
      return
    }
    if (profileQuery.data.revisionNumber === baseline.revisionNumber) return
    if (isDirty) {
      setServerHasNewerRevision(true)
      return
    }
    setBaseline(profileQuery.data)
    setDraft(profileToDraft(profileQuery.data))
  }, [baseline, draft, isDirty, profileQuery.data])

  const blocker = useBlocker({
    disabled: !isDirty,
    enableBeforeUnload: isDirty,
    withResolver: true,
    shouldBlockFn: () => true,
  })

  const validationErrors = useMemo(
    () => (draft ? validateCompanyProfileDraft(draft, isDirty) : {}),
    [draft, isDirty],
  )
  const canLookupGUS = Boolean(draft && isValidPolishNip(draft.taxId))

  const acceptSavedProfile = async (
    profile: CompanyProfileDTO,
    description = `Utworzono rewizję ${profile.revisionNumber}.`,
  ) => {
    setBaseline(profile)
    setDraft(profileToDraft(profile))
    setErrors({})
    setRevisionConflict(null)
    setServerHasNewerRevision(false)
    setSaveOutcomeUnknown(false)
    setLabelLogoFile(null)
    setLabelLogoFileReady(false)
    queryClient.setQueryData(["companyProfile", "authenticated"], profile)
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["companyProfile", "public"] }),
      queryClient.invalidateQueries({
        queryKey: ["companyProfile", "revisions"],
      }),
    ])
    toast({
      title: "Profil firmy został zapisany",
      description,
      status: "success",
      duration: 3000,
      isClosable: true,
    })
  }

  const updateMutation = useMutation({
    mutationFn: (attempt: CompanyProfileSaveAttempt) => {
      if (attempt.draft.labelLogoSource === "upload" && attempt.labelLogoFile) {
        return CompanyProfileService.uploadLabelLogo(
          attempt.labelLogoFile,
          draftToUpdateRequest(attempt.draft, attempt.expectedRevisionNumber),
        )
      }
      if (
        attempt.draft.labelLogoSource === "upload" &&
        baseline?.labelLogoSource !== "upload"
      ) {
        throw new Error("Wybierz plik logo przed zapisaniem zmian.")
      }
      return CompanyProfileService.updateProfile(
        draftToUpdateRequest(attempt.draft, attempt.expectedRevisionNumber),
      )
    },
    onSuccess: async (profile) => acceptSavedProfile(profile),
    onError: async (error: any, attempt) => {
      if (error?.body?.code === "company_profile_revision_conflict") {
        setRevisionConflict(error.body.currentRevisionNumber)
        return
      }
      const status = Number(error?.status ?? 0)
      if (!status || status >= 500) {
        const result = await profileQuery.refetch()
        if (
          result.data &&
          result.data.revisionNumber > attempt.expectedRevisionNumber
        ) {
          const serverDraft = profileToDraft(result.data)
          const attemptedDraft = attempt.labelLogoFile
            ? { ...attempt.draft, labelLogo: serverDraft.labelLogo }
            : attempt.draft
          if (!isCompanyProfileDirty(serverDraft, attemptedDraft)) {
            await acceptSavedProfile(
              result.data,
              `Potwierdzono zapisaną rewizję ${result.data.revisionNumber} po ponownym połączeniu.`,
            )
            return
          }
          setServerHasNewerRevision(true)
        } else {
          setSaveOutcomeUnknown(true)
        }
      }
      toast({
        title: "Nie udało się zapisać profilu firmy",
        description:
          status > 0 && status < 500
            ? (error?.body?.error ?? "Sprawdź dane i spróbuj ponownie.")
            : "Nie udało się potwierdzić wyniku zapisu. Szkic pozostał bez zmian.",
        status: "error",
        duration: 4500,
        isClosable: true,
      })
    },
  })

  const gusLookupMutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error("Profil firmy nie jest gotowy.")
      return CompanyProfileService.lookupByGUS({
        nip: draft.taxId.replace(/\D/g, ""),
      })
    },
    onSuccess: (result) => {
      setGUSResult(result)
      gusDialog.onOpen()
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się pobrać danych z GUS",
        description:
          error?.body?.error ??
          error?.message ??
          "Sprawdź NIP i spróbuj ponownie.",
        status: "error",
        duration: 4500,
        isClosable: true,
      })
    },
  })

  const exportMutation = useMutation({
    mutationFn: () =>
      ClientDataExportApi.downloadArchive({ includeFiles: true }),
    onSuccess: ({ blob, fileName }) => {
      downloadBlob(blob, fileName)
      toast({
        title: "Archiwum danych zostało przygotowane",
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    },
    onError: (error: Error) => {
      toast({
        title: "Nie udało się pobrać danych klienta",
        description: error.message,
        status: "error",
        duration: 4500,
        isClosable: true,
      })
    },
  })

  const updateField = (field: CompanyProfileField, value: string) => {
    setDraft((current) => (current ? { ...current, [field]: value } : current))
    if (errors[field]) {
      setErrors((current) => ({ ...current, [field]: undefined }))
    }
    setRevisionConflict(null)
    setSaveOutcomeUnknown(false)
  }

  const save = () => {
    if (!draft || !baseline || !isDirty) return
    if (labelLogoUploadInvalid) return
    const nextErrors = validateCompanyProfileDraft(draft, true)
    setErrors(nextErrors)
    const firstError = Object.keys(nextErrors)[0]
    if (firstError) {
      requestAnimationFrame(() => {
        document.getElementById(`company-${firstError}`)?.focus()
        document.getElementById(`company-${firstError}`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      })
      return
    }
    if (
      labelLogoFile &&
      (!["image/png", "image/jpeg", "image/webp"].includes(
        labelLogoFile.type,
      ) ||
        labelLogoFile.size > 2 * 1024 * 1024)
    ) {
      toast({
        title: "Nieprawidłowy plik logo",
        description: "Wybierz plik PNG, JPEG lub WebP o rozmiarze do 2 MiB.",
        status: "error",
        duration: 4000,
        isClosable: true,
      })
      return
    }
    updateMutation.mutate({
      draft: { ...draft },
      expectedRevisionNumber: baseline.revisionNumber,
      labelLogoFile,
    })
  }

  const discard = () => {
    if (!baseline) return
    setDraft(profileToDraft(baseline))
    setErrors({})
    setRevisionConflict(null)
    setServerHasNewerRevision(false)
    setSaveOutcomeUnknown(false)
    setLabelLogoFile(null)
    setLabelLogoFileReady(false)
  }

  const reloadServerVersion = async () => {
    const result = await profileQuery.refetch()
    if (result.data) {
      setBaseline(result.data)
      setDraft(profileToDraft(result.data))
      setErrors({})
      setRevisionConflict(null)
      setServerHasNewerRevision(false)
      setSaveOutcomeUnknown(false)
      setLabelLogoFile(null)
      setLabelLogoFileReady(false)
    }
  }

  if (profileQuery.isPending) {
    return (
      <Container maxW="6xl" py={4}>
        <Stack spacing={4} aria-label="Ładowanie profilu firmy">
          <Skeleton height="120px" borderRadius="2xl" />
          <Skeleton height="360px" borderRadius="2xl" />
        </Stack>
      </Container>
    )
  }

  if (profileQuery.isError || !baseline || !draft) {
    return (
      <Container maxW="4xl" py={8}>
        <Alert status="error" borderRadius="xl" alignItems="start">
          <AlertIcon mt={1} />
          <Box flex="1">
            <AlertTitle>Nie udało się pobrać profilu firmy</AlertTitle>
            <AlertDescription>
              Formularz pozostaje zablokowany, aby nie nadpisać zapisanych
              danych.
            </AlertDescription>
            <Button mt={4} size="sm" onClick={() => profileQuery.refetch()}>
              Spróbuj ponownie
            </Button>
          </Box>
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxW="7xl" py={{ base: 4, lg: 3 }}>
      <Stack spacing={4}>
        <WmsPageHeader
          title={baseline.legalName || "Profil firmy"}
          description="Dane prawne, kontaktowe i branding używane przez Stockerra oraz przyszłe wydruki."
          gradient="linear(to-r, teal.600, green.600)"
          badge={`Rewizja ${baseline.revisionNumber}`}
          actions={
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<FiMoreVertical />}
                aria-label="Więcej działań profilu firmy"
                size="sm"
                variant="outline"
                bg="white"
                color="teal.700"
                borderColor="white"
              />
              <MenuList color="gray.800">
                <MenuItem
                  icon={<FiDownload />}
                  isDisabled={exportMutation.isPending}
                  onClick={() => exportMutation.mutate()}
                >
                  Pobierz dane klienta
                </MenuItem>
              </MenuList>
            </Menu>
          }
        />

        {serverHasNewerRevision || revisionConflict !== null ? (
          <Alert status="warning" borderRadius="xl" alignItems="start">
            <AlertIcon mt={1} />
            <Box flex="1">
              <AlertTitle>
                Profil firmy został zmieniony przez innego administratora
              </AlertTitle>
              <AlertDescription>
                Twój szkic pozostał bez zmian. Wczytaj aktualną wersję przed
                dalszą edycją
                {revisionConflict
                  ? ` (bieżąca rewizja: ${revisionConflict}).`
                  : "."}
              </AlertDescription>
              <Button
                mt={3}
                size="sm"
                colorScheme="orange"
                onClick={reloadServerVersion}
              >
                Wczytaj aktualną wersję
              </Button>
            </Box>
          </Alert>
        ) : null}

        {saveOutcomeUnknown ? (
          <Alert status="warning" borderRadius="xl" alignItems="start">
            <AlertIcon mt={1} />
            <Box flex="1">
              <AlertTitle>Nie udało się potwierdzić wyniku zapisu</AlertTitle>
              <AlertDescription>
                Twój szkic pozostał bez zmian. Sprawdź połączenie i wczytaj
                aktualny profil przed ponowną próbą.
              </AlertDescription>
              <Button mt={3} size="sm" onClick={reloadServerVersion}>
                Wczytaj aktualny profil
              </Button>
            </Box>
          </Alert>
        ) : null}

        <Tabs isLazy colorScheme="teal" variant="enclosed" size="sm">
          <TabList overflowX="auto" overflowY="hidden">
            <Tab whiteSpace="nowrap">Dane firmy</Tab>
            <Tab whiteSpace="nowrap">Historia zmian</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} pb={isDirty ? 24 : 0}>
              <Flex gap={5} align="start">
                <Stack
                  display={{ base: "none", lg: "flex" }}
                  position="sticky"
                  top={4}
                  minW="210px"
                  bg={panelBg}
                  borderWidth="1px"
                  borderColor={panelBorder}
                  borderRadius="xl"
                  p={3}
                  spacing={1}
                  aria-label="Sekcje profilu firmy"
                >
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    fontWeight="bold"
                    px={3}
                    py={2}
                  >
                    SEKCJE
                  </Text>
                  {sectionLinks.map(([id, label]) => (
                    <Button
                      key={id}
                      as="a"
                      href={`#${id}`}
                      variant="ghost"
                      justifyContent="flex-start"
                      size="sm"
                    >
                      {label}
                    </Button>
                  ))}
                </Stack>
                <Box flex="1" minW={0}>
                  <CompanyProfileForm
                    draft={draft}
                    errors={isDirty ? validationErrors : errors}
                    canLookupGUS={canLookupGUS}
                    isGUSLoading={gusLookupMutation.isPending}
                    onChange={updateField}
                    onLookupGUS={() => gusLookupMutation.mutate()}
                    labelLogoFile={labelLogoFile}
                    onLabelLogoFileChange={(file) => {
                      setLabelLogoFile(file)
                      setLabelLogoFileReady(false)
                      setRevisionConflict(null)
                    }}
                    onLabelLogoFileReadyChange={setLabelLogoFileReady}
                    isLabelLogoFileRequired={requiresNewLabelLogoFile}
                  />
                  {isDirty ? (
                    <Box
                      mt={5}
                      bg={panelBg}
                      borderWidth="1px"
                      borderColor={panelBorder}
                      borderRadius="2xl"
                      p={4}
                    >
                      <FormControl
                        isRequired
                        isInvalid={Boolean(validationErrors.changeReason)}
                        size="sm"
                      >
                        <FormLabel htmlFor="company-changeReason" fontSize="sm">
                          Powód zmiany
                        </FormLabel>
                        <Textarea
                          id="company-changeReason"
                          value={draft.changeReason}
                          onChange={(event) =>
                            updateField("changeReason", event.target.value)
                          }
                          placeholder="Np. aktualizacja adresu siedziby"
                          maxLength={1000}
                          size="sm"
                          borderRadius="xl"
                        />
                        <FormHelperText fontSize="xs">
                          Opis będzie widoczny w historii rewizji.
                        </FormHelperText>
                        <FormErrorMessage fontSize="xs">
                          {validationErrors.changeReason}
                        </FormErrorMessage>
                      </FormControl>
                    </Box>
                  ) : null}
                </Box>
              </Flex>
            </TabPanel>
            <TabPanel px={0}>
              <Box
                bg={panelBg}
                borderWidth="1px"
                borderColor={panelBorder}
                borderRadius="2xl"
                p={{ base: 4, md: 5 }}
              >
                <Heading size="md" mb={1}>
                  Historia zmian
                </Heading>
                <Text color="gray.500" mb={4}>
                  Niezmienny rejestr profilu używanego w brandingu i wydrukach.
                </Text>
                <CompanyProfileHistory
                  currentRevision={baseline.revisionNumber}
                />
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Stack>

      {isDirty ? (
        <Flex
          position="fixed"
          left={{ base: 3, md: "auto" }}
          right={{ base: 3, md: 6 }}
          bottom={4}
          zIndex={20}
          bg={stickyBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="2xl"
          boxShadow="xl"
          p={3}
          gap={3}
          align="center"
          justify="space-between"
          wrap="wrap"
          aria-label="Niezapisane zmiany"
        >
          <Box>
            <Text fontWeight="semibold">Masz niezapisane zmiany</Text>
            <Text fontSize="sm" color="gray.500">
              {Object.keys(validationErrors).length > 0
                ? "Uzupełnij oznaczone pola przed zapisem."
                : "Zmiany są gotowe do zapisania."}
            </Text>
          </Box>
          <Flex gap={2} flex={{ base: "1 1 100%", sm: "0 0 auto" }}>
            <Button
              leftIcon={<FiRotateCcw />}
              variant="ghost"
              size="sm"
              flex={{ base: 1, sm: "none" }}
              onClick={discard}
            >
              Odrzuć
            </Button>
            <Button
              leftIcon={<FiSave />}
              colorScheme="teal"
              size="sm"
              flex={{ base: 1, sm: "none" }}
              isLoading={updateMutation.isPending}
              isDisabled={
                updateMutation.isPending ||
                Object.keys(validationErrors).length > 0 ||
                revisionConflict !== null ||
                serverHasNewerRevision ||
                labelLogoUploadInvalid
              }
              onClick={save}
            >
              Zapisz zmiany
            </Button>
          </Flex>
        </Flex>
      ) : null}

      <CompanyProfileGUSDialog
        isOpen={gusDialog.isOpen}
        result={gusResult}
        draft={draft}
        onClose={gusDialog.onClose}
        onApply={(values) => {
          setDraft((current) =>
            current
              ? {
                  ...current,
                  ...values,
                  changeReason:
                    current.changeReason ||
                    "Aktualizacja danych na podstawie GUS",
                }
              : current,
          )
          gusDialog.onClose()
          toast({
            title: "Wybrane dane GUS wpisano do formularza",
            description: "Sprawdź zmiany i zapisz profil firmy.",
            status: "success",
            duration: 3000,
            isClosable: true,
          })
        }}
      />

      {blocker.status === "blocked" ? (
        <ConfirmDialog
          isOpen
          title="Odrzucić niezapisane zmiany?"
          message="Masz niezapisane zmiany profilu firmy. Opuszczenie strony spowoduje ich utratę."
          confirmLabel="Opuść stronę"
          cancelLabel="Zostań"
          confirmColorScheme="red"
          onClose={() => blocker.reset()}
          onConfirm={() => blocker.proceed()}
        />
      ) : null}
    </Container>
  )
}
